import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Download, ZoomIn } from "lucide-react";

/**
 * Full-screen image lightbox. Click outside, hit Escape, or tap the X to close.
 * Used for visual cards on dynamic pages so admins can drop full-resolution
 * flyers in without worrying about the card-tile crop hiding details.
 */
export default function ImageLightbox({ src, alt, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!src) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-ink/92 backdrop-blur-md flex items-center justify-center p-4 animate-[fade-up_.25s_ease-out_both]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="image-lightbox"
    >
      {/* Close + download */}
      <div className="absolute top-5 right-5 flex items-center gap-2">
        <a
          href={src}
          download
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          title="Open original"
          className="w-10 h-10 rounded-full bg-cream/15 hover:bg-cream/25 text-cream flex items-center justify-center transition-colors"
        >
          <Download size={18} />
        </a>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          aria-label="Close image"
          data-testid="image-lightbox-close"
          className="w-10 h-10 rounded-full bg-cream/15 hover:bg-cream/25 text-cream flex items-center justify-center transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <img
        src={src}
        alt={alt || ""}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[96vw] max-h-[92vh] w-auto h-auto rounded-xl shadow-2xl object-contain"
      />
    </div>,
    document.body
  );
}

/** Small helper component for the click hint overlay on hoverable images. */
export function ZoomHintOverlay() {
  return (
    <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/30 transition-colors flex items-center justify-center pointer-events-none">
      <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cream/95 text-ink text-[11px] font-semibold uppercase tracking-wider">
        <ZoomIn size={12} /> View full size
      </span>
    </div>
  );
}
